import z from "zod";

import { AddressSchema } from "./address";
import { BusinessSchema } from "./business";
import { nullableToOptional } from "./utils";

const TimeSchema = z.object({
  hour: z.number(),
  minute: z.number(),
  second: z.number(),
  nano: z.number(),
});

export const ShiftSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  address: AddressSchema,
  business: BusinessSchema,
  wage: z.number(),
  repeat: nullableToOptional(z.string()),
  days: z.array(z.string()).optional(),
  status: z
    .enum([
      "DRAFT",
      "PUBLISHED",
      "APPLICATIONS",
      "ASSIGNED",
      "CONFIRMED",
      "IN_PROGRESS",
      "COMPLETED",
      "CANCELLED",
      "NO_SHOW",
    ])
    .optional(),
  role_name: z.string(),
  role_description: z.string().optional(),
  start_date: z.string(),
  end_date: z.string(),
  start_time: TimeSchema,
  end_time: TimeSchema,
  min_rating: z.number().optional(),
  located_within: z.number().optional(),
  allow_instant_acceptance: z.boolean().optional().default(false),
  require_training: z.boolean().optional().default(false),
});

export type Shift = z.infer<typeof ShiftSchema>;

export const ShiftSchemaResponse = z
  .object({
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().nullable(),
    id: z.number(),
    name: z.string().nullable(),
    business: BusinessSchema,
    // Tolerate a missing/malformed address so one sparse shift can't crash the whole
    // page (e.g. the employer dashboard's offers list). Falls back to an empty address.
    address: AddressSchema.catch({}),
    location: z.string(),
    wage: z.number(),
    repeat: z.string().nullable(),
    status: z.enum([
      "DRAFT",
      "PUBLISHED",
      "APPLICATIONS",
      "ASSIGNED",
      "CONFIRMED",
      "IN_PROGRESS",
      "COMPLETED",
      "CANCELLED",
      "NO_SHOW",
    ]),
    latitude: z.number().catch(0),
    longitude: z.number().catch(0),
    role_name: z.string(),
    role_description: z.string().nullable(),
    wage_unit: z.string().nullable(),
    start_date: z.string(),
    end_date: z.string(),
    start_time: z.string(),
    end_time: z.string(),
    days: z.array(z.string()).nullable(),
    /** Days posted together as one multi-day block share this; null otherwise. */
    series_id: nullableToOptional(z.string()),
    min_rating: z.number().catch(0),
    located_within: nullableToOptional(z.number()),
    allow_instant_acceptance: z.boolean().catch(false),
    require_training: z.boolean().catch(false),
  })
  .passthrough();

export type ShiftResponse = z.infer<typeof ShiftSchemaResponse>;

export const ShiftListSchemaResponse = z.object({
  message: z.string().nullable(),
  success: z.boolean().default(false),
  shifts: z.array(ShiftSchemaResponse),
  total: z.number(),
});

export type ShiftListResponse = z.infer<typeof ShiftListSchemaResponse>;

export const ShiftSearchListSchemaResponse = z.object({
  message: z.string().nullable(),
  success: z.boolean().default(false),
  shifts: z.array(ShiftSchemaResponse),
  total: z.number(),
});

export type ShiftSearchListResponse = z.infer<
  typeof ShiftSearchListSchemaResponse
>;
